import { useState, useCallback } from 'react';
import { LabelTemplate, HistoryState } from '@/types/label';

const MAX_HISTORY_SIZE = 50;

export const useHistory = (initialTemplate: LabelTemplate) => {
  const [history, setHistory] = useState<HistoryState[]>([
    { template: initialTemplate, timestamp: Date.now() },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const pushState = useCallback((template: LabelTemplate) => {
    setHistory((prev) => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, currentIndex + 1);
      
      // Add new state
      newHistory.push({
        template: JSON.parse(JSON.stringify(template)),
        timestamp: Date.now(),
      });

      // Limit history size
      if (newHistory.length > MAX_HISTORY_SIZE) {
        newHistory.shift();
        setCurrentIndex(newHistory.length - 1);
        return newHistory;
      }

      setCurrentIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [currentIndex]);

  const undo = useCallback((): LabelTemplate | null => {
    if (!canUndo) return null;
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    return JSON.parse(JSON.stringify(history[newIndex].template));
  }, [canUndo, currentIndex, history]);

  const redo = useCallback((): LabelTemplate | null => {
    if (!canRedo) return null;
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    return JSON.parse(JSON.stringify(history[newIndex].template));
  }, [canRedo, currentIndex, history]);

  const clearHistory = useCallback((template: LabelTemplate) => {
    setHistory([{ template: JSON.parse(JSON.stringify(template)), timestamp: Date.now() }]);
    setCurrentIndex(0);
  }, []);

  return {
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    historyLength: history.length,
    currentIndex,
    clearHistory,
  };
};
